/**
 * A utility for integrating with Google Classroom.  It supports adding a
 * share button to the UI so that content can be added to Google Classroom.
 *
 * We are using explicit injection so that we can use a React component to
 * display the button on a whim.  The script is an externally loaded script
 * that must be loaded each time (no bundling for us).
 *
 * Because we can't make calls to the Google Classroom servers from localhost,
 * it can be difficult to locally test Google Classroom functionality. But this
 * file includes some tips for how to stub out certain functions to skip these
 * production calls - look for "GOOGLE CLASSROOM MANUAL TESTING" to learn more.
 */

/**
 * TODO(slim): Remove this eslint-disable when we update this file to use
 * Learning Time ConversionContexts.
 */
// eslint-disable-next-line no-restricted-imports -- see above
import * as BigBingoInternals from "@khan/analytics-learning-time/bigbingo-internals";
import {Errors, KAError} from "@khan/logging";
import {InitialRequestUrl, RequestInfo} from "@khan/shared-core";

import type {IClassroomAPI, IGoogleAPI, IListAPI} from "./google-api.ts";
import {
    authorizeWithImplicitGrant,
    loadGoogleGAPIClient,
} from "./google-util.ts";

const CLASSROOM_SHARE_URL = "https://classroom.google.com/share";

export const GC_CLIENT_ID = () =>
    RequestInfo.IS_DEV_SERVER
        ? "179486897809-nedb177f1iatc8dra55e9g4ei2d908mo.apps.googleusercontent.com"
        : "179486897809-vot6i8d4ohsqvv7rui9vbjd53k6eo5mo.apps.googleusercontent.com";

const GC_PAGE_SIZE = 100;
const GC_MAX_PAGE_COUNT = 10;

export interface GoogleCourse {
    id: string;
    name: string;
    alternateLink: string;
}

export interface GoogleStudent {
    profile: {
        emailAddress: string;
        name: {
            fullName: string;
        };
    };
}

function windowGapi(): IGoogleAPI {
    return window["gapi"];
}

const ROSTER_SCOPES = [
    "https://www.googleapis.com/auth/classroom.courses.readonly",
    "https://www.googleapis.com/auth/classroom.rosters.readonly",
    "https://www.googleapis.com/auth/classroom.profile.emails",
];

const SHARE_SCOPES = [
    "https://www.googleapis.com/auth/classroom.coursework.students",
];

// Only users inside the various elements of the Google Classroom Progress
// prototype should be prompted for sharing and coursework-related scopes.
function allScopes(): Array<string> {
    const scopes = [...ROSTER_SCOPES];

    return scopes;
}

export const GoogleClassroom = {
    ROSTER_SCOPES,
    SHARE_SCOPES,
    allScopes,

    _gapiPromise: undefined,

    // helper method to allow for unit test stubbing
    _getHostname(): string {
        return InitialRequestUrl.hostname.toLowerCase();
    },

    canLoadGoogleApi(): boolean {
        // Pubcommit hostnames are used to select the publish version. The most
        // common usage is for running e2e tests against a publish version as
        // part of the publishing process. Unfortunately we can't use static or
        // pubcommit hostnames with google classroom since we cannot specify
        // origin hostnames with wildcards.
        const isStaticJSDomain = this._getHostname().startsWith("static-");
        const isPubCommitDomain = this._getHostname().startsWith("pubcommit-");
        const isProdDomain = this._getHostname().endsWith(".khanacademy.org");

        return (
            RequestInfo.IS_DEV_SERVER ||
            (isProdDomain && !(isStaticJSDomain || isPubCommitDomain))
        );
    },

    loadGoogleAPI(): Promise<IGoogleAPI> {
        // Load the Google API.  As soon as it is done, try to do an
        // "immediate" authorization.  This will make the token available
        // if the user has done this recently enough.
        if (!this.canLoadGoogleApi()) {
            return Promise.reject(
                new KAError(
                    "Google Classroom not enabled for user",
                    Errors.NotAllowed,
                ),
            );
        }

        if (!this._gapiPromise) {
            // Load gapi to work with the Google Classroom API.
            return loadGoogleGAPIClient();
        }

        return this._gapiPromise;
    },

    classroomAPI(): Promise<IClassroomAPI> {
        const gapi = windowGapi();

        if (!gapi || !gapi.client) {
            return Promise.reject(
                new KAError(
                    "Google APIs need to be loaded. Call loadGoogleAPI",
                    Errors.NotAllowed,
                ),
            );
        }

        if (gapi.client.classroom) {
            return Promise.resolve(gapi.client.classroom);
        }

        return new Promise<IClassroomAPI>((resolve, reject) => {
            gapi.client?.load("classroom", "v1", (response) => {
                if (response && response.error) {
                    reject(response.error);
                }

                if (gapi.client?.classroom) {
                    resolve(gapi.client.classroom);
                }

                reject(
                    new KAError(
                        "gapi.client.classroom couldn't be loaded",
                        Errors.Internal,
                    ),
                );
            });
        });
    },

    _queryPagedList(
        api: IListAPI,
        responseSelector: any,
        data: any = {},
        callCount = 0,
    ): Promise<Array<any>> {
        if (callCount >= GC_MAX_PAGE_COUNT) {
            return Promise.resolve([]);
        }

        const requestData = {
            ...data,
            pageSize: GC_PAGE_SIZE,
        } as const;

        return new Promise((resolve, reject) => {
            // @ts-expect-error - TS2554 - Expected 0 arguments, but got 1.
            api.list(requestData).execute((response: PagedResponse) => {
                if (!response || response.error) {
                    return reject(
                        new KAError(
                            "Failed to get Google Classroom response",
                            Errors.TransientService,
                            {
                                cause:
                                    response?.error ||
                                    "Did not receive a response",
                            },
                        ),
                    );
                }

                if (!response.nextPageToken) {
                    return resolve(responseSelector(response) || []);
                }

                const nextPage = this._queryPagedList(
                    api,
                    responseSelector,
                    {
                        ...data,
                        pageToken: response.nextPageToken,
                    },
                    callCount + 1,
                ).then((list) => [
                    ...(responseSelector(response) || []),
                    ...list,
                ]);

                resolve(nextPage);
            });
        });
    },

    getCourses(): Promise<Array<GoogleCourse>> {
        // GOOGLE CLASSROOM MANUAL TESTING
        // To manually test the Google Classroom integration, consider
        // uncommenting the following block
        // return Promise.all([{
        // 	id: "123",
        // 	name: "My Google Class Name",
        // 	alternateLink: "https://example.com",
        // }]);
        return authorizeWithImplicitGrant(GC_CLIENT_ID(), ROSTER_SCOPES)
            .then(() => this.classroomAPI())
            .then((classroomAPI: IClassroomAPI) =>
                this._queryPagedList(
                    classroomAPI.courses,
                    (response) => response.courses,
                    {
                        courseStates: ["ACTIVE"],
                        teacherId: "me",
                    },
                ),
            );
    },

    getStudents(courseId: string): Promise<Array<GoogleStudent>> {
        // GOOGLE CLASSROOM MANUAL TESTING
        // To manually test the Google Classroom integration, consider
        // uncommenting the following block
        //return Promise.all([
        //	{
        //		profile: {
        //			emailAddress: "test@example.org",
        //			name: {
        //				fullName: "Sync Student",
        //			}
        //		},
        //	},
        //	{
        //		profile: {
        //			emailAddress: "oops not an email",
        //			name: {
        //				fullName: "Error student",
        //			}
        //		},
        //	}
        //])
        if (!courseId) {
            return Promise.reject(
                new KAError(
                    "getStudents(courseId) missing parameter!",
                    Errors.InvalidInput,
                ),
            );
        }

        // Occassionally google will provide us an invalid user that
        // doesn't appear in the google classroom list.
        function isValidUser(user: any) {
            const emailAddress = user.profile.emailAddress;
            return !!emailAddress;
        }

        // Sometimes google will return an invitation without a userId
        // property, this will cause the `getUserProfile` call to fail and
        // break importing for a class.
        function isValidInvitation(invitation: any) {
            return !!invitation.userId;
        }

        return authorizeWithImplicitGrant(GC_CLIENT_ID(), ROSTER_SCOPES)
            .then(() => this.classroomAPI())
            .then((classroomAPI: IClassroomAPI) => {
                function getUserProfile(invitation: any) {
                    return classroomAPI.userProfiles
                        .get({
                            userId: invitation.userId,
                        })
                        .then((response) => {
                            const result = {
                                ...invitation,
                                profile: response.result,
                                isPending: true,
                            } as const;

                            // remove the invitation id
                            delete result.id;

                            return result;
                        });
                }

                // Fetch the students
                const students = this._queryPagedList(
                    classroomAPI.courses.students,
                    (response) => response.students,
                    {courseId},
                );

                // Fetch the invitations
                const invitations = this._queryPagedList(
                    classroomAPI.invitations,
                    (response) => response.invitations,
                    {courseId},
                ).then((invitations) => {
                    // Invitations don't get profiles, we can ask for
                    // profiles, but only once at a time, so go fetch
                    // the user profiles in parallel and add them to the
                    // invitations
                    return Promise.all(
                        invitations
                            .filter(isValidInvitation)
                            .map(getUserProfile),
                    );
                });

                // Get students and invitations in parallel
                return Promise.all([students, invitations]);
            })
            .then(([students, invitations]: [any, any]) => [
                ...students,
                ...invitations,
            ])
            .then((users) => users.filter(isValidUser));
    },

    getShareLink(url: string): string {
        // Change http://someplace.com/path to
        // http://someplace.com/gclassroom/path
        // For tracking purposes
        const urlElement = document.createElement("a");
        urlElement.href = url;

        const host = `${urlElement.protocol}//${urlElement.host}`;

        return `${host}/gclassroom${urlElement.pathname}${urlElement.search}`;
    },

    markSharingConversion(shareLink: string) {
        BigBingoInternals.markConversion("content_shared_to_google_classroom", {
            shareLink,
        });
    },

    shareToClassroom(url: string, title: string, body: string) {
        const shareLink = this.getShareLink(url);
        let params = `url=${encodeURIComponent(shareLink)}`;
        params += `&title=${encodeURIComponent(title)}`;
        params += `&body=${encodeURIComponent(body)}`;

        this.markSharingConversion(shareLink);

        window.open(
            `${CLASSROOM_SHARE_URL}?${params}`,
            // @ts-expect-error - TS2345 - Argument of type 'null' is not assignable to parameter of type 'string | undefined'.
            null,
            "width=640, height=450,toolbar=no, scrollbars=yes",
        );
    },
} as const;
